import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { useLocale } from "@/lib/LocaleContext";
import { useEntitlement } from "@/lib/useEntitlement";
import { localisedTo } from "@/lib/i18nRoutes";

/**
 * Compact header pill showing the customer's remaining free-download balance.
 * Renders nothing when:
 *   - user is not logged in
 *   - the summary is still loading (avoids flicker)
 *   - the summary fetch failed (avoids nagging red states in the header)
 *
 * Sizes: pill is compact enough for the header row and shrinks further on
 * mobile via responsive padding + text sizes. RTL-safe (uses logical
 * margin classes and doesn't hard-code direction).
 */
export default function BalancePill() {
  const { user } = useAuth();
  const { lang } = useLocale();
  const { t } = useTranslation();
  const { summary } = useEntitlement();

  // Hide entirely for logged-out visitors and admins (admin roles use the
  // dashboard for entitlement admin work, not their own customer balance).
  if (!user) return null;
  if (user.role === "admin") return null;
  if (!summary) return null;

  const remaining = Number(summary.remaining ?? 0);
  const label =
    remaining === 1
      ? t("music.balance.headerOne")
      : t("music.balance.headerMany", { count: remaining });

  const zero = remaining === 0;

  return (
    <Link
      to={localisedTo(zero ? "/music/premium" : "/music/free", lang)}
      data-testid="balance-pill"
      data-testid-remaining={remaining}
      title={t("music.balance.tooltip")}
      className={[
        "hidden sm:inline-flex items-center gap-1.5",
        "font-mono text-[10px] tracking-[0.2em] uppercase",
        "px-2.5 py-1 rounded-full border transition-colors",
        zero
          ? "border-red-brand/60 text-red-brand/90 hover:border-red-brand hover:text-red-brand"
          : "border-white/15 text-white/70 hover:border-white/40 hover:text-white",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={`inline-block w-1.5 h-1.5 rounded-full ${
          zero ? "bg-red-brand" : "bg-white/60"
        }`}
      />
      {label}
    </Link>
  );
}
